import {UVTemplateLine, UVTemplateOptions} from '../../i';

// Pure helpers for the UV template config (settingsJSON of a FrameParts UV template,
// EntityTypeID = 20). No store imports: the add-to-cart precheck and its tests use them.

// Default per-line input cap (the single limit uv-print.tsx used before max_length existed).
export const UV_DEFAULT_MAX_LENGTH = 50;

export const defaultUVOptions = (): UVTemplateOptions => ({required: false, lock_font: false, lock_color: false, photo_width: 0, photo_height: 0});

// The "options" object next to uv_lines in a template's settingsJSON. Anything missing
// or malformed falls back to the optional UV-on-mat behaviour.
export const parseUVOptions = (raw: unknown): UVTemplateOptions => {
    const d = defaultUVOptions();

    if (!raw || typeof raw !== 'object') {
        return d;
    }

    const o = raw as Record<string, unknown>;
    const inches = (v: unknown): number => (typeof v === 'number' && isFinite(v) && v > 0 ? v : 0);

    return {
        required: o.required === true,
        lock_font: o.lock_font === true,
        lock_color: o.lock_color === true,
        photo_width: inches(o.photo_width),
        photo_height: inches(o.photo_height),
    };
};

// A static line is fixed template text (its value), never an input, always printed.
export const isStaticUVLine = (ln: UVTemplateLine | null | undefined): boolean => !!ln && ln.static === true;

export const uvLineMaxLength = (ln: UVTemplateLine | null | undefined): number => (ln && typeof ln.max_length === 'number' && ln.max_length > 0 ? Math.floor(ln.max_length) : UV_DEFAULT_MAX_LENGTH);

// CSS font-weight of a line (template `weight`, 400 when unset).
export const uvLineWeight = (ln: {weight?: number} | null | undefined): number => (ln && typeof ln.weight === 'number' && ln.weight > 0 ? ln.weight : 400);

export const uvLineTitle = (ln: UVTemplateLine): string => ln.title || `Line ${ln.i}`;

// Same wording as the server's custom-field check (ProductATC.cs validation.customFields).
export const requiredUVLineMessage = (title: string): string => `The ${title} field is required. Please enter the required information.`;

// Add-to-cart precheck for a required template: the first editable, non-optional line
// without text names the error. '' = nothing blocks.
export const getRequiredUVLineError = (options: UVTemplateOptions | null | undefined, lines: UVTemplateLine[] | null | undefined, isUVProduct: boolean): string => {
    if (!isUVProduct || !options || !options.required) {
        return '';
    }

    for (const ln of lines || []) {
        if (!ln || isStaticUVLine(ln) || ln.optional === true) {
            continue;
        }

        if (!(ln.value || '').trim()) {
            return requiredUVLineMessage(uvLineTitle(ln));
        }
    }

    return '';
};

// The UVContent JSON posted at add-to-cart and carried by the cart-image state: the
// lines plus the template options (ImageGenerator2 reads photo_width / photo_height
// from it). Without options the JSON is exactly what it was before options existed.
export const uvContentJSON = (lines: UVTemplateLine[], options: UVTemplateOptions | null | undefined): string => JSON.stringify(options ? {uv_lines: lines, options} : {uv_lines: lines});
